/**
 * PluginBlock — 文档内 ::render[id] 的顶层分派器。
 *
 * 职责（精简后，P3 #5）：
 *   1. Scope chain：scoped definition / scoped content（依赖 useScopeContext，
 *      不能下沉到 resolveAndRender，因为它是上下文相关、不是 id-keyed）。
 *   2. 否则按 for-block / content（含 use-execution 派生 + ContentBlockWrapper）
 *      回退 —— 委托给 resolveAndRender。
 *   3. 否则若 root definition 命中 → 渲染 PluginRenderSection（独立文件）。
 *   4. 都没命中 → 渲染未找到/加载占位符。
 *
 * 历史的「PluginRenderSection」（Renderer + 工具栏 + 错误/进度 UI）已抽离到
 * 同目录的 PluginRenderSection.tsx；本文件只承担 dispatch。
 */

import { splitScopedMemberPath } from "@magic-md/core";
import type { ForBlock as ForBlockType, ForDefinition, RenderMode } from "@magic-md/core";
import { useT } from "@magic-md/i18n/react";
import { ContinuationProvider } from "@magic-md/plugin-sdk";
import type { RendererConfigChangeOptions, RendererProps } from "@magic-md/plugin-sdk";
import { AlertTriangle, Loader2, Play } from "lucide-react";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ComponentType } from "react";
import { cn } from "../../../lib/utils";
import {
    resolveRenderResolutionIssue,
    type RenderResolutionIssue,
} from "../../../preview-logic";
import { getThemeAccentStyle } from "../../../theme/runtime/theme-accents";
import { useDocRendererContinuation } from "../../continuation/ContinuationResolver";
import { PluginErrorBoundary } from "../../PluginErrorBoundary";
import {
    findContentDefinitionInScope,
    findDefinitionInScope,
    findForDefinitionInScope,
    findOutputInScope,
    useScopeContext,
} from "../../ScopeContext";
import { useDocContext, useDocOutput } from "../contexts";
import {
    useContentExecution,
    useDefinitionExecutionState,
    useExecutionSessionConnected,
    useImportExecution,
    useUseExecution,
} from "../contexts/ExecutionContext";
import {
    CollapsedUnitRenderHeader,
    getPluginDisplayName,
    getPluginTechnicalLabel,
    type CollapsedUnitRenderStatus,
} from "./CollapsedUnitRenderHeader";
import { ContentBlock } from "./ContentBlock";
import { ForBlock } from "./ForBlock";
import { PluginRenderEmptyState, PluginRenderSection } from "./PluginRenderSection";
import { getRendererAttrs, readRenderExpanded } from "./render-attrs";
import { RenderUnitBadge } from "./RenderUnitBadge";
import { resolveAndRender } from "./resolveAndRender";
import { TemplateRenderPlaceholder } from "./TemplateRenderPlaceholder";

function isExternalStubOutput(output: unknown): boolean {
    if (!output || typeof output !== "object" || Array.isArray(output)) return false;
    const keys = Object.keys(output as Record<string, unknown>);
    return keys.length === 1 && keys[0] === "__external";
}

function forDefinitionToRenderBlock(
    id: string,
    forDefinition: ForDefinition,
): ForBlockType | null {
    const children = forDefinition.innerScope.blocks;
    if (!children) return null;
    return {
        type: "for-block",
        id,
        each: forDefinition.each,
        of: forDefinition.of,
        limit: forDefinition.limit,
        offset: forDefinition.offset,
        concurrency: forDefinition.concurrency,
        template: forDefinition.template,
        params: forDefinition.params,
        inputSchema: forDefinition.inputSchema,
        externalRef: forDefinition.externalRef,
        children,
    };
}

function RenderResolutionWarning({
    issue,
    mode,
    isRunning,
    onRunImport,
}: {
    issue: RenderResolutionIssue;
    mode: RenderMode;
    isRunning: boolean;
    onRunImport?: (namespace: string) => void;
}) {
    const t = useT("editor");
    const accentStyle = getThemeAccentStyle("warning");
    const canRunImport = !!issue.canRunImport && !!issue.namespace && !!onRunImport;

    if (mode === "inline") {
        return (
            <span
                className="inline-flex max-w-full items-center gap-1 rounded border px-1.5 py-0.5 align-baseline text-xs font-mono theme-accent-chip"
                style={accentStyle}
                title={`${issue.title}: ${issue.message}${issue.hint ? ` ${issue.hint}` : ""}`}
                data-render-diagnostic={issue.code}
            >
                <AlertTriangle size={12} aria-hidden="true" />
                <span className="truncate">{issue.targetId}</span>
            </span>
        );
    }

    return (
        <div
            className="my-1.5 rounded-lg border border-dashed bg-background px-3 py-2.5 transition-colors theme-accent-banner"
            style={accentStyle}
            data-render-diagnostic={issue.code}
        >
            <div className="flex min-w-0 items-center gap-3">
                <RenderUnitBadge main="R" sub={issue.badgeSub} />
                <div className="min-w-0 flex-1">
                    <div className="flex min-w-0 items-center gap-2">
                        <span className="truncate font-mono text-[13px] font-medium text-foreground">
                            {issue.targetId}
                        </span>
                        <span
                            className="shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium theme-accent-chip"
                            style={accentStyle}
                        >
                            warning
                        </span>
                    </div>
                    <div className="mt-0.5 flex min-w-0 items-center gap-1.5 text-[11px] text-muted-foreground">
                        <span className="shrink-0">render</span>
                        <span className="text-muted-foreground/40">·</span>
                        <span className="truncate">{issue.title}</span>
                        <span
                            className="hidden min-w-0 truncate font-mono text-muted-foreground/50 sm:inline"
                            title={issue.code}
                        >
                            {issue.code}
                        </span>
                    </div>
                    <div
                        className="mt-1 truncate text-[11px] theme-accent-text"
                        style={accentStyle}
                        title={issue.hint ? `${issue.message} ${issue.hint}` : issue.message}
                    >
                        {issue.message}
                        {issue.hint ? ` ${issue.hint}` : ""}
                    </div>
                </div>

                <AlertTriangle
                    size={15}
                    className="shrink-0 theme-accent-text"
                    style={accentStyle}
                    aria-label="render warning"
                />

                {canRunImport && (
                    <button
                        type="button"
                        data-magic-md-input-confirm="true"
                        className={cn(
                            "inline-flex h-7 shrink-0 items-center gap-1.5 rounded-md border border-border px-2.5 text-[11px] font-medium",
                            "transition-colors",
                            isRunning
                                ? "cursor-wait bg-muted text-muted-foreground"
                                : "bg-primary text-primary-foreground hover:bg-primary/90",
                        )}
                        onClick={() => {
                            if (issue.namespace) onRunImport?.(issue.namespace);
                        }}
                        disabled={isRunning}
                        aria-label={t("render.runImport", { namespace: issue.namespace ?? "" })}
                    >
                        {isRunning ? (
                            <Loader2 size={13} className="animate-spin" aria-hidden="true" />
                        ) : (
                            <Play size={13} aria-hidden="true" />
                        )}
                        <span>
                            {isRunning
                                ? t("status.running")
                                : t("render.runImport", { namespace: issue.namespace ?? "" })}
                        </span>
                    </button>
                )}
            </div>
        </div>
    );
}

export const PluginBlock = memo(function PluginBlock({
    definitionId,
    attrs,
    mode,
    defaultExpanded = true,
}: {
    definitionId: string;
    attrs: Record<string, string>;
    mode: RenderMode;
    defaultExpanded?: boolean;
}) {
    const t = useT("editor");
    const doc = useDocContext();
    const docOutput = useDocOutput(definitionId);
    const unitExecution = useDefinitionExecutionState(definitionId);
    const sessionConnected = useExecutionSessionConnected();
    const scope = useScopeContext();
    const continuationCtx = useDocRendererContinuation(mode);

    const handleAction = useCallback(
        async (actionName: string, params?: Record<string, unknown>) => {
            if (doc.executable && doc.onAction) {
                return doc.onAction(definitionId, actionName, params);
            }
            return undefined;
        },
        [definitionId, doc.executable, doc.onAction],
    );
    const handleApi = useCallback(
        async <TData = unknown,>(apiName: string, params?: Record<string, unknown>) => {
            if (doc.executable && doc.onApi) {
                return doc.onApi<TData>(definitionId, apiName, params);
            }
            return {
                success: false,
                message: "Plugin runtime API is not available",
            };
        },
        [definitionId, doc.executable, doc.onApi],
    );
    const handleConfigChange = useCallback(
        async (nextConfig: Record<string, unknown>, options?: RendererConfigChangeOptions) => {
            if (!doc.onDefinitionConfigChange) return undefined;
            if (!options) return doc.onDefinitionConfigChange(definitionId, nextConfig);
            return doc.onDefinitionConfigChange(definitionId, nextConfig, options);
        },
        [definitionId, doc.onDefinitionConfigChange],
    );
    const handleScopedRun = useCallback(() => {
        doc.onRun?.(definitionId);
    }, [definitionId, doc.onRun]);

    const isInline = mode === "inline";
    const Wrapper = isInline ? "span" : "div";
    const initialExpanded = readRenderExpanded(attrs, defaultExpanded);
    const [expanded, setExpanded] = useState(initialExpanded);
    const rendererAttrs = getRendererAttrs(attrs);
    const importOutputRequestKeyRef = useRef<string | null>(null);
    const importBindingForTarget = useMemo(() => {
        const slashIdx = definitionId.indexOf("/");
        const namespace = slashIdx > 0
            ? definitionId.slice(0, slashIdx)
            : definitionId;
        const binding = doc.importBindings?.find((item) => item.namespace === namespace);
        if (!binding?.externalRef) return null;
        if (slashIdx < 0) {
            if (!binding.document || doc.importDocuments?.has(namespace)) return null;
            return binding;
        }
        if (doc.definitions.has(definitionId)) return null;
        return binding;
    }, [definitionId, doc.definitions, doc.importBindings, doc.importDocuments]);
    const scopedContentExecution = useContentExecution(definitionId);
    const definition = doc.definitions.get(definitionId);
    const renderIssue = !definition
        ? resolveRenderResolutionIssue({
            targetId: definitionId,
            imports: doc.importBindings,
            importDocuments: doc.importDocuments,
            mode,
        })
        : null;
    const renderIssueImportExecution = useImportExecution(renderIssue?.namespace ?? null);
    const fallbackContentMember = useMemo(
        () =>
            splitScopedMemberPath(
                definitionId,
                (id) => !!doc.contentDefinitions?.has(id),
                (id) =>
                    doc.definitions.has(id) ||
                    !!doc.forBlocksMap?.has(id) ||
                    !!doc.contentDefinitions?.has(id) ||
                    !!doc.importDocuments?.has(id),
            ),
        [
            definitionId,
            doc.contentDefinitions,
            doc.definitions,
            doc.forBlocksMap,
            doc.importDocuments,
        ],
    );
    const fallbackExecutionId = fallbackContentMember?.rootId ?? definitionId;
    const fallbackUseExecution = useUseExecution(fallbackExecutionId);
    const fallbackContentExecution = useContentExecution(fallbackExecutionId);

    useEffect(() => {
        setExpanded(initialExpanded);
    }, [definitionId, initialExpanded]);

    useEffect(() => {
        if (!importBindingForTarget?.externalRef || !doc.onLoadOutput) return;
        const requestKey = `${importBindingForTarget.namespace}\u0000${importBindingForTarget.externalRef.protocol}\u0000${importBindingForTarget.externalRef.locator}`;
        if (importOutputRequestKeyRef.current === requestKey) return;
        importOutputRequestKeyRef.current = requestKey;
        doc.onLoadOutput(importBindingForTarget.namespace).catch(() => {});
    }, [doc.onLoadOutput, importBindingForTarget]);

    // ── 1. Scope chain: scoped definition ──
    const scopedDefinition = findDefinitionInScope(scope, definitionId);
    const scopedOutput = findOutputInScope(scope, definitionId);
    const scopedContentDefinition = findContentDefinitionInScope(scope, definitionId);
    const scopedForDefinition = findForDefinitionInScope(scope, definitionId);

    if (scopedDefinition) {
        const plugin = doc.registry.get(scopedDefinition.pluginName);
        const output = isExternalStubOutput(scopedOutput)
            ? (docOutput ?? scopedDefinition.output ?? scopedOutput)
            : (scopedOutput ?? docOutput ?? scopedDefinition.output);

        if (scopedDefinition.template) {
            return (
                    <TemplateRenderPlaceholder
                        unitId={definitionId}
                        kindLabel={t("render.kind.definition")}
                    technicalLabel={scopedDefinition.pluginName}
                    params={scopedDefinition.params}
                    mode={mode}
                    badgeMain="D"
                    badgeSub="T"
                />
            );
        }

        if (!isInline && plugin?.Renderer && output && !expanded) {
            const outputError = output && typeof output === "object" && !Array.isArray(output)
                ? (output as Record<string, unknown>).__error
                : undefined;
            const outputErrorMsg = typeof outputError === "object" && outputError !== null
                ? (outputError as { message?: string }).message
                : null;
            const execErrorMsg = unitExecution.errorMessage ?? null;
            const errorMessage = outputErrorMsg || execErrorMsg || null;
            const collapsedStatus: CollapsedUnitRenderStatus = errorMessage
                ? "error"
                : unitExecution.isRunning
                    ? "running"
                    : unitExecution.status === "success"
                        ? "success"
                        : null;
            return (
                <Wrapper>
                    <CollapsedUnitRenderHeader
                        unitId={definitionId}
                        label={getPluginDisplayName(plugin.manifest)}
                        technicalLabel={getPluginTechnicalLabel(plugin.manifest)}
                        status={collapsedStatus}
                        statusLabels={errorMessage ? { error: errorMessage } : undefined}
                        onExpand={() => setExpanded(true)}
                    />
                </Wrapper>
            );
        }

        if (!plugin?.Renderer || !output) {
            const ScopeWrapper = isInline ? "span" : "div";
            const canRun = !!doc.onRun && !!plugin;
            const isRunning = unitExecution.isRunning;
            const isPluginLoading = !plugin &&
                !!doc.registry.isLoading?.(scopedDefinition.pluginName);
            const errorMessage = unitExecution.errorMessage ?? null;
            const hasError = !!errorMessage;
            const missingOutputText = !plugin
                ? isPluginLoading
                    ? t("render.placeholder.loadingPlugin")
                    : `Plugin "${scopedDefinition.pluginName}" not installed`
                : !plugin.Renderer
                    ? t("render.placeholder.noRenderer")
                    : t("render.placeholder.notRun");
            const statusLabel = isRunning
                ? t("status.running")
                : hasError
                    ? t("render.status.failed")
                    : !plugin
                        ? isPluginLoading
                            ? t("status.loading")
                            : t("render.status.notInstalled")
                        : !plugin.Renderer
                            ? t("render.status.notRenderable")
                            : t("render.status.notRun");

            if (isInline) {
                return (
                    <button
                        type="button"
                        data-magic-md-input-confirm="true"
                        className={`inline-flex items-center gap-1 text-xs font-mono px-1.5 py-0.5 rounded border-none ${hasError ? "theme-accent-button" : "bg-muted hover:bg-muted/80"} ${canRun ? "cursor-pointer" : "cursor-default"}`}
                        style={hasError ? getThemeAccentStyle("error") : undefined}
                        onClick={canRun ? handleScopedRun : undefined}
                        title={errorMessage ?? missingOutputText}
                    >
                        {isRunning || isPluginLoading ? (
                            <span
                                className="inline-flex h-2 w-2 rounded-full animate-pulse theme-accent-fill"
                                style={getThemeAccentStyle("running")}
                            />
                        ) : hasError ? (
                            <span
                                className="theme-accent-text"
                                style={getThemeAccentStyle("error")}
                            >
                                ⚠
                            </span>
                        ) : canRun ? (
                            <span className="text-primary">▶</span>
                        ) : null}
                        {definitionId}
                    </button>
                );
            }

            return (
                <ScopeWrapper>
                    <PluginRenderEmptyState
                        definitionId={definitionId}
                        pluginDisplayName={plugin
                            ? getPluginDisplayName(plugin.manifest)
                            : scopedDefinition.pluginName}
                        pluginTechnicalLabel={plugin
                            ? getPluginTechnicalLabel(plugin.manifest)
                            : scopedDefinition.pluginName}
                        unitBadge={{ main: "D" }}
                        statusLabel={statusLabel}
                        message={missingOutputText}
                        canRun={canRun}
                        isRunning={isRunning}
                        isLoading={isPluginLoading}
                        hasError={hasError}
                        errorMessage={errorMessage}
                        onRun={handleScopedRun}
                    />
                </ScopeWrapper>
            );
        }

        const Renderer = plugin.Renderer as ComponentType<RendererProps & {
            onConfigChange?: RendererProps["onConfigChange"];
        }>;
        return (
            <Wrapper className={isInline ? "inline" : undefined}>
                <PluginErrorBoundary
                    pluginName={scopedDefinition.pluginName}
                    definitionId={definitionId}
                >
                    <ContinuationProvider value={continuationCtx}>
                        <Renderer
                            config={scopedDefinition.config}
                            output={output}
                            mode={mode}
                            attrs={rendererAttrs}
                            definitionId={definitionId}
                            documentPath={doc.documentPath}
                            documentBundlePath={doc.documentBundlePath}
                            onAction={handleAction}
                            callApi={handleApi}
                            onConfigChange={doc.onDefinitionConfigChange ? handleConfigChange : undefined}
                            callSidecar={doc.callSidecar}
                            onEngineNotification={doc.onEngineNotification}
                            selectFile={doc.selectFile}
                            selectFolder={doc.selectFolder}
                            openExternal={doc.openExternal}
                            getAssetUrl={doc.getAssetUrl}
                            getDefinitions={doc.getDefinitions}
                            getTabs={doc.getTabs}
                            editMode={doc.editMode ?? false}
                        />
                    </ContinuationProvider>
                </PluginErrorBoundary>
            </Wrapper>
        );
    }

    // ── 2. Scope chain: scoped for definition ──
    if (scopedForDefinition) {
        const scopedForBlock = forDefinitionToRenderBlock(definitionId, scopedForDefinition);
        if (scopedForBlock) {
            return (
                <ForBlock
                    block={scopedForBlock}
                    mode={mode}
                    attrs={attrs}
                    defaultExpanded={defaultExpanded}
                />
            );
        }
    }

    // ── 3. Scope chain: scoped content definition ──
    if (scopedContentDefinition) {
        return (
            <ContentBlock
                content={scopedContentDefinition}
                definitionId={definitionId}
                execution={scopedContentExecution}
                mode={mode}
                attrs={attrs}
                defaultExpanded={defaultExpanded}
                onRun={
                    doc.onRunUse || doc.onRunContent
                        ? () => {
                            if (doc.onRunUse) {
                                doc.onRunUse(definitionId);
                            } else {
                                doc.onRunContent?.(definitionId);
                            }
                        }
                        : undefined
                }
                instanceKind="scoped-content"
            />
        );
    }

    // ── 4. Root definition lookup ──
    if (renderIssue?.code === "magic-md/invalid-inline-import-render") {
        return (
            <RenderResolutionWarning
                issue={renderIssue}
                mode={mode}
                isRunning={renderIssueImportExecution?.status === "running"}
                onRunImport={doc.onRunImport}
            />
        );
    }

    // ── 5. For-block / content (use-execution aware) fallback ──
    // Both delegated to resolveAndRender for unified id→component routing.
    // The `definition` short-circuit above ensures we don't recursively re-enter
    // PluginBlock through resolveAndRender's "definition" branch.
    if (!definition) {
        const fallback = resolveAndRender(
            {
                targetId: definitionId,
                mode,
                attrs,
                defaultExpanded,
                useExecution: fallbackUseExecution,
                contentExecution: fallbackContentExecution,
                wrapContent: true,
            },
            doc,
        );
        if (fallback) return fallback;
    }

    // ── 6. Definition not found ──
    if (!definition) {
        // When the session isn't connected yet, the definition may still be loading
        // (e.g., ::use blocks referencing imported namespaces with external output stubs)
        if (!sessionConnected) {
            if (isInline) {
                return (
                    <span className="inline-flex items-center gap-1 text-xs font-mono px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
                        <span className="inline-flex h-2 w-2 rounded-full animate-pulse bg-muted-foreground/40" />
                        {definitionId}
                    </span>
                );
            }
            return (
                <div className="my-1.5 rounded-lg border border-dashed border-border bg-muted/30 p-3 flex items-center justify-center gap-3">
                    <span className="inline-flex h-3 w-3 rounded-full animate-pulse bg-muted-foreground/30" />
                    <span className="text-sm text-muted-foreground">
                        <code className="font-mono text-xs bg-muted px-1.5 py-0.5 rounded">
                            {definitionId}
                        </code>
                        {" — loading…"}
                    </span>
                </div>
            );
        }
        if (isInline) {
            return (
                <RenderResolutionWarning
                    issue={renderIssue ?? resolveRenderResolutionIssue({
                        targetId: definitionId,
                        imports: doc.importBindings,
                        importDocuments: doc.importDocuments,
                        mode,
                    })}
                    mode={mode}
                    isRunning={
                        !!renderIssue?.namespace &&
                        renderIssueImportExecution?.status === "running"
                    }
                    onRunImport={doc.onRunImport}
                />
            );
        }
        const issue = renderIssue ?? resolveRenderResolutionIssue({
            targetId: definitionId,
            imports: doc.importBindings,
            importDocuments: doc.importDocuments,
            mode,
        });
        return (
            <RenderResolutionWarning
                issue={issue}
                mode={mode}
                isRunning={
                    !!issue.namespace &&
                    renderIssueImportExecution?.status === "running"
                }
                onRunImport={doc.onRunImport}
            />
        );
    }

    // ── 7. Plugin render section (definition exists) ──
    return (
        <PluginRenderSection
            definitionId={definitionId}
            attrs={attrs}
            mode={mode}
            defaultExpanded={defaultExpanded}
        />
    );
});
